import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmt, hasPerm, initials } from "@/lib/helpers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Trash2, RotateCcw, Search, Pencil, UserPlus, HandCoins, SlidersHorizontal, ChevronUp, ChevronDown, Eye, Download, Save } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { StudentForm } from "@/pages/Students";
import CollectFeeDialog from "@/pages/CollectFeeDialog";
import { toast } from "sonner";

const COL_KEYS = ["student", "adm", "class", "address", "phone", "session", "slip", "promise", "due", "reason", "removed", "remark"];
const COLS_STORAGE = "sssps_left_cols_v1";

function EditableText({ value, onSave, placeholder, testid }) {
  const [v, setV] = useState(value || "");
  useEffect(() => { setV(value || ""); }, [value]);
  return (
    <Input value={v} placeholder={placeholder} onChange={(e) => setV(e.target.value)}
      onBlur={() => { if ((v || "") !== (value || "")) onSave(v); }}
      className="h-7 text-xs px-1.5 w-full min-w-[160px]" data-testid={testid} />
  );
}

function PromiseInline({ s, onSave }) {
  const [date, setDate] = useState(s.promise_date || "");
  const [amount, setAmount] = useState(s.promise_amount ?? "");
  useEffect(() => { setDate(s.promise_date || ""); setAmount(s.promise_amount ?? ""); }, [s.promise_date, s.promise_amount]);
  const save = () => {
    if ((date || "") !== (s.promise_date || "") || Number(amount || 0) !== Number(s.promise_amount || 0))
      onSave(date || "", Number(amount || 0));
  };
  return (
    <div className="flex flex-col gap-1 w-28">
      <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} onBlur={save} className="h-7 text-xs px-1.5" data-testid={`promise-date-${s.admission_number}`} />
      <Input type="number" placeholder="Amount" value={amount} onChange={(e) => setAmount(e.target.value)} onBlur={save} className="h-7 text-xs px-1.5 tabular" data-testid={`promise-amount-${s.admission_number}`} />
    </div>
  );
}

function DueInline({ s, editable, onSave }) {
  const due = Number(s.fees_summary?.due || 0);
  const [v, setV] = useState(due);
  useEffect(() => { setV(due); }, [due]);
  if (!editable) return <span className={`font-semibold tabular ${due > 0 ? "text-rose-600" : "text-emerald-600"}`}>{fmt(due)}</span>;
  return (
    <Input type="number" value={v} onChange={(e) => setV(e.target.value)}
      onBlur={() => { if (Number(v || 0) !== due) onSave(Number(v || 0)); }}
      className={`h-7 text-xs px-1.5 w-24 tabular font-semibold ${Number(v || 0) > 0 ? "text-rose-600" : "text-emerald-600"}`}
      data-testid={`due-${s.admission_number}`} />
  );
}

export default function DeletedStudents() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [permTarget, setPermTarget] = useState(null);
  const [collectTarget, setCollectTarget] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [fClass, setFClass] = useState("all");
  const [fAddress, setFAddress] = useState("");
  const [fSession, setFSession] = useState("all");
  const [fPhone, setFPhone] = useState("");
  const [editTarget, setEditTarget] = useState(null);
  const [settings, setSettings] = useState({});
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [restoreClassId, setRestoreClassId] = useState("");
  const [restoring, setRestoring] = useState(false);
  const loadSettings = useCallback(() => {
    api.get("/settings").then((r) => setSettings(r.data || {})).catch(() => {});
  }, []);
  useEffect(() => { loadSettings(); }, [loadSettings]);
  const [colOrder, setColOrder] = useState(() => {
    try {
      const s = JSON.parse(localStorage.getItem(COLS_STORAGE));
      if (s?.order) {
        const valid = s.order.filter((k) => COL_KEYS.includes(k));
        return [...valid, ...COL_KEYS.filter((k) => !valid.includes(k))];
      }
    } catch { /* ignore */ }
    return COL_KEYS;
  });
  const [hiddenCols, setHiddenCols] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(COLS_STORAGE))?.hidden || []); } catch { return new Set(); }
  });
  useEffect(() => {
    localStorage.setItem(COLS_STORAGE, JSON.stringify({ order: colOrder, hidden: [...hiddenCols] }));
  }, [colOrder, hiddenCols]);
  const moveCol = (key, dir) => setColOrder((o) => {
    const i = o.indexOf(key); const j = i + dir;
    if (j < 0 || j >= o.length) return o;
    const n = [...o]; [n[i], n[j]] = [n[j], n[i]]; return n;
  });
  const toggleCol = (key) => setHiddenCols((h) => { const n = new Set(h); n.has(key) ? n.delete(key) : n.add(key); return n; });
  const resetCols = () => { setColOrder(COL_KEYS); setHiddenCols(new Set()); };

  const load = useCallback(() => {
    setLoading(true);
    api.get("/students/deleted").then((r) => setRows(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/classes").then((r) => setClasses(r.data)).catch(() => {}); }, []);

  const openEdit = (s) => { setEditTarget(s); setFormOpen(true); };
  const openAdd = () => { setEditTarget(null); setFormOpen(true); };
  const markLeft = async (newStu) => {
    try {
      await api.delete(`/students/${newStu.id}`, { data: { reason: "", leave_status: "Left", leave_date: "" } });
    } catch (e) {
      toast.error("Student created but could not be moved to Left Students");
    }
  };
  const patchField = async (id, field, value) => {
    try {
      await api.patch(`/students/${id}/field`, { field, value });
      setRows((r) => r.map((x) => (x.id === id ? { ...x, [field]: value } : x)));
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const patchPromise = async (id, date, amount) => {
    try {
      await api.patch(`/students/${id}/meta`, { promise_date: date, promise_amount: amount });
      setRows((r) => r.map((x) => (x.id === id ? { ...x, promise_date: date, promise_amount: amount } : x)));
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const patchDue = async (id, due) => {
    try {
      await api.patch(`/students/${id}/deleted-due`, { due });
      setRows((r) => r.map((x) => (x.id === id ? { ...x, fees_summary: { ...(x.fees_summary || {}), due } } : x)));
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const doRestore = async () => {
    if (!restoreTarget || !restoreClassId) return;
    const cls = classes.find((c) => c.id === restoreClassId);
    setRestoring(true);
    try {
      const r = await api.post(`/students/${restoreTarget.id}/restore`, { dest_class_id: restoreClassId, dest_section: cls?.section || "" });
      const carried = r.data?.old_dues_carried || 0;
      toast.success(carried > 0 ? `Student restored · ${fmt(carried)} moved to Old Fees` : "Student restored");
      setRestoreTarget(null); setRestoreClassId(""); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setRestoring(false); }
  };

  const permanentDelete = async () => {
    if (!permTarget) return;
    try {
      await api.delete(`/students/${permTarget.id}/permanent`);
      toast.success("Student permanently deleted"); setPermTarget(null); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const q = search.toLowerCase();
  const sessionOpts = Array.from(new Set(rows.map((s) => s.academic_session).filter(Boolean)));
  const filtered = rows.filter((s) => {
    if (q && ![s.name, s.admission_number, s.father_name, s.deleted_reason].some((v) => (v || "").toLowerCase().includes(q))) return false;
    if (fClass !== "all" && s.class_id !== fClass) return false;
    if (fSession !== "all" && (s.academic_session || "") !== fSession) return false;
    if (fAddress && !(s.address || "").toLowerCase().includes(fAddress.toLowerCase())) return false;
    if (fPhone && !(s.phone || "").toLowerCase().includes(fPhone.toLowerCase())) return false;
    return true;
  });

  const canRestore = hasPerm(user, "edit_student");
  const currentSession = settings?.academic_session || "";
  const _destClasses = currentSession ? classes.filter((c) => (c.academic_session || "") === currentSession) : classes;
  const destClasses = _destClasses.length ? _destClasses : classes;
  const canDelete = hasPerm(user, "delete");
  const canCollect = hasPerm(user, "collect_fee");
  const isAdmin = user?.role === "admin";
  const canSettings = isAdmin || hasPerm(user, "manage_settings");
  const totalDue = filtered.reduce((sum, s) => sum + (Number(s.fees_summary?.due) || 0), 0);

  const dash = <span className="text-slate-300">—</span>;
  const colMap = {
    student: { label: "Student", cell: (s) => (
      <div className="flex items-center gap-2.5">
        <div className="w-9 h-9 rounded-full bg-slate-100 text-slate-500 overflow-hidden flex items-center justify-center text-xs font-semibold shrink-0 border border-slate-200">
          {s.photo ? <img src={s.photo} alt={s.name} className="w-full h-full object-cover" /> : initials(s.name)}
        </div>
        <div><div className="font-medium text-slate-800">{s.name}</div>{s.father_name && <div className="text-xs text-slate-400">S/o {s.father_name}</div>}</div>
      </div>) },
    adm: { label: "Adm No", tdClass: "text-slate-500", cell: (s) => s.admission_number },
    class: { label: "Class", tdClass: "text-slate-500", cell: (s) => `${s.class_name} ${s.section}` },
    address: { label: "Address", tdClass: "text-slate-600 max-w-[180px] truncate", title: (s) => s.address, cell: (s) => s.address || dash },
    phone: { label: "Phone", tdClass: "text-slate-600", cell: (s) => s.phone || dash },
    session: { label: "Session", tdClass: "text-slate-600", cell: (s) => s.academic_session || dash },
    slip: { label: "Slip No.", tdClass: "text-slate-600", cell: (s) => s.slip_no || dash },
    promise: { label: "Promise (Date / Amt)", stop: true, cell: (s) => <PromiseInline s={s} onSave={(d, a) => patchPromise(s.id, d, a)} /> },
    due: { label: "Due at Removal", stop: true, cell: (s) => <DueInline s={s} editable={isAdmin && settings.left_due_editable !== false} onSave={(d) => patchDue(s.id, d)} /> },
    reason: { label: "Reason", tdClass: "text-slate-600", cell: (s) => s.deleted_reason || dash },
    removed: { label: "Removed On", tdClass: "text-slate-500", cell: (s) => (s.deleted_at ? s.deleted_at.slice(0, 10) : "—") },
    remark: { label: "Remark", stop: true, cell: (s) => <EditableText value={s.remark} placeholder="Remark…" onSave={(v) => patchField(s.id, "remark", v)} testid={`remark-${s.admission_number}`} /> },
  };
  const visibleCols = colOrder.filter((k) => !hiddenCols.has(k));

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl font-extrabold text-slate-900 flex items-center gap-2"><Trash2 className="text-rose-500" size={26} />Left Students</h1>
          <p className="text-slate-500">{rows.length} removed student{rows.length !== 1 ? "s" : ""} · restore any record anytime</p>
        </div>
      </div>

      <Tabs defaultValue="left" className="w-full">
        <TabsList data-testid="left-tabs">
          <TabsTrigger value="left" data-testid="tab-left">Left Students</TabsTrigger>
          <TabsTrigger value="fees" data-testid="tab-fees">Fees Status</TabsTrigger>
          {canSettings && <TabsTrigger value="settings" data-testid="tab-settings">Settings</TabsTrigger>}
        </TabsList>

        <TabsContent value="left" className="space-y-5 pt-4">
      <div className="flex items-center justify-end gap-3 flex-wrap">
        <div className="flex items-center gap-2 rounded-xl border border-rose-200 bg-rose-50 px-4 py-2 mr-auto" data-testid="total-due-banner">
          <span className="text-sm font-medium text-rose-700">Total Due at Removal{filtered.length !== rows.length ? " (filtered)" : ""}:</span>
          <span className="text-lg font-extrabold tabular text-rose-700" data-testid="total-due-amount">{fmt(totalDue)}</span>
        </div>
        {canRestore && (
          <Button className="gap-1.5 shrink-0" onClick={openAdd} data-testid="add-left-student-btn">
            <UserPlus size={16} />Add Left Student
          </Button>
        )}
      </div>
      <div className="bg-white rounded-xl border border-slate-200 p-3">
        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input className="pl-9 h-9" placeholder="Search name, adm no, reason…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="deleted-search" />
          </div>
          <Select value={fClass} onValueChange={setFClass}>
            <SelectTrigger className="h-9 w-40" data-testid="filter-class"><SelectValue placeholder="Class" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Classes</SelectItem>
              {classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={fSession} onValueChange={setFSession}>
            <SelectTrigger className="h-9 w-36" data-testid="filter-session"><SelectValue placeholder="Session" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sessions</SelectItem>
              {sessionOpts.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input className="h-9 w-40" placeholder="Address…" value={fAddress} onChange={(e) => setFAddress(e.target.value)} data-testid="filter-address" />
          <Input className="h-9 w-36" placeholder="Phone…" value={fPhone} onChange={(e) => setFPhone(e.target.value)} data-testid="filter-phone" />
          {(fClass !== "all" || fSession !== "all" || fAddress || fPhone) && (
            <Button variant="ghost" size="sm" className="h-9 text-slate-500" onClick={() => { setFClass("all"); setFSession("all"); setFAddress(""); setFPhone(""); }} data-testid="filter-clear">Clear</Button>
          )}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="h-9 gap-1.5 ml-auto" data-testid="columns-btn"><SlidersHorizontal size={15} />Columns</Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-72 p-2" data-testid="columns-popover">
              <div className="flex items-center justify-between px-1 pb-2 mb-1 border-b border-slate-100">
                <span className="text-sm font-semibold text-slate-700">Show / Arrange Columns</span>
                <button onClick={resetCols} className="text-xs text-indigo-600 hover:underline" data-testid="columns-reset">Reset</button>
              </div>
              <div className="max-h-80 overflow-y-auto space-y-0.5">
                {colOrder.map((k, idx) => (
                  <div key={k} className="flex items-center gap-2 px-1 py-1 rounded hover:bg-slate-50" data-testid={`col-row-${k}`}>
                    <Checkbox checked={!hiddenCols.has(k)} onCheckedChange={() => toggleCol(k)} data-testid={`col-toggle-${k}`} />
                    <span className="flex-1 text-sm text-slate-700">{colMap[k].label}</span>
                    <button disabled={idx === 0} onClick={() => moveCol(k, -1)} className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30" data-testid={`col-up-${k}`}><ChevronUp size={15} /></button>
                    <button disabled={idx === colOrder.length - 1} onClick={() => moveCol(k, 1)} className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30" data-testid={`col-down-${k}`}><ChevronDown size={15} /></button>
                  </div>
                ))}
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 table-scroll">
        {loading ? <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-indigo-500" /></div> :
          filtered.length === 0 ? (
            <div className="py-16 text-center text-slate-400"><Trash2 size={40} className="mx-auto mb-2 opacity-40" />No deleted students.</div>
          ) : (
            <div className="overflow-auto max-h-[calc(100vh-300px)]">
              <table className="w-full text-sm border border-slate-200 border-collapse" style={{ minWidth: `${visibleCols.length * 130 + 240}px` }}>
                <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider sticky top-0 z-10">
                  <tr>
                    {visibleCols.map((k) => (
                      <th key={k} className="px-3 py-2.5 border border-slate-200 bg-slate-50 text-left">{colMap[k].label}</th>
                    ))}
                    <th className="px-3 py-2.5 border border-slate-200 bg-slate-50 text-center sticky right-0 z-20">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((s) => (
                    <tr key={s.id} className="hover:bg-slate-50" data-testid={`deleted-row-${s.admission_number}`}>
                      {visibleCols.map((k) => {
                        const col = colMap[k];
                        return (
                          <td key={k} className={`px-3 py-2.5 border border-slate-200 ${col.tdClass || ""}`} title={col.title ? col.title(s) : undefined} onClick={col.stop ? (e) => e.stopPropagation() : undefined}>
                            {col.cell(s)}
                          </td>
                        );
                      })}
                      <td className="px-3 py-2.5 border border-slate-200 sticky right-0 bg-white z-10">
                        <div className="grid grid-cols-2 gap-1.5 w-[220px]">
                          {canCollect && (
                            <Button size="sm" variant="outline" className="gap-1.5 text-indigo-700 border-indigo-200 hover:bg-indigo-50" onClick={() => nav(`/fees/collect/${s.id}`)} data-testid={`collect-fees-${s.admission_number}`}>
                              <HandCoins size={14} />Collect
                            </Button>
                          )}
                          {canRestore && (
                            <Button size="sm" variant="outline" className="gap-1.5" onClick={() => openEdit(s)} data-testid={`edit-left-${s.admission_number}`}>
                              <Pencil size={14} />Edit
                            </Button>
                          )}
                          {canRestore && (
                            <Button size="sm" variant="outline" className="gap-1.5 text-emerald-700 border-emerald-200 hover:bg-emerald-50" onClick={() => { setRestoreTarget(s); setRestoreClassId(""); }} data-testid={`restore-${s.admission_number}`}>
                              <RotateCcw size={14} />Restore
                            </Button>
                          )}
                          {canDelete && (
                            <Button size="sm" variant="outline" className="gap-1.5 text-rose-700 border-rose-200 hover:bg-rose-50" onClick={() => setPermTarget(s)} data-testid={`perm-delete-${s.admission_number}`}>
                              <Trash2 size={14} />Delete
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </div>

        </TabsContent>

        <TabsContent value="fees" className="pt-4">
          <FeesStatusTab />
        </TabsContent>

        {canSettings && (
          <TabsContent value="settings" className="pt-4">
            <LeftSettingsTab settings={settings} onSaved={loadSettings} />
          </TabsContent>
        )}
      </Tabs>

      <StudentForm
        open={formOpen}
        onOpenChange={(o) => { setFormOpen(o); if (!o) setEditTarget(null); }}
        classes={classes}
        edit={editTarget}
        addTitle="Add Left Student"
        onAdded={markLeft}
        onSaved={load}
      />

      <CollectFeeDialog
        open={!!collectTarget}
        onOpenChange={(o) => !o && setCollectTarget(null)}
        studentId={collectTarget?.id}
        onCollected={load}
      />

      <AlertDialog open={!!permTarget} onOpenChange={(o) => !o && setPermTarget(null)}>
        <AlertDialogContent data-testid="perm-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-rose-600">Permanently Delete Student?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove <b>{permTarget?.name}</b> ({permTarget?.admission_number}) and all their fee records. This action <b>cannot be undone</b> and the student cannot be restored afterwards.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="perm-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-rose-600 hover:bg-rose-700" onClick={permanentDelete} data-testid="perm-delete-confirm">Delete Permanently</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!restoreTarget} onOpenChange={(o) => !o && setRestoreTarget(null)}>
        <AlertDialogContent data-testid="restore-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-emerald-700">Restore Student</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-1 text-slate-600">
                <div>Restoring <b>{restoreTarget?.name}</b> ({restoreTarget?.admission_number}){restoreTarget?.academic_session ? <> from session <b>{restoreTarget.academic_session}</b></> : null}.</div>
                <div>They'll be moved into the current session{currentSession ? <> <b>{currentSession}</b></> : ""} under the class you choose. Any outstanding dues from the previous session{Number(restoreTarget?.fees_summary?.due) > 0 ? <> (<b>{fmt(Number(restoreTarget?.fees_summary?.due))}</b>)</> : ""} will be rolled into <b>Old Fees</b>, and all payment history &amp; receipts are kept.</div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-1">
            <Label className="text-xs text-slate-500 mb-1 block">Destination Class &amp; Section</Label>
            <Select value={restoreClassId} onValueChange={setRestoreClassId}>
              <SelectTrigger data-testid="restore-class-select"><SelectValue placeholder="Select class…" /></SelectTrigger>
              <SelectContent>
                {destClasses.map((c) => (
                  <SelectItem key={c.id} value={c.id} data-testid={`restore-class-opt-${c.id}`}>{c.name}{c.section ? ` - ${c.section}` : ""}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {destClasses.length === 0 && <p className="text-xs text-rose-500 mt-1">No classes found. Please create classes for the current session first.</p>}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="restore-cancel">Cancel</AlertDialogCancel>
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5" disabled={!restoreClassId || restoring} onClick={doRestore} data-testid="restore-confirm">{restoring ? <Loader2 className="animate-spin" size={14} /> : <RotateCcw size={14} />}Restore</Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function FeesStatusTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("left_page");
  const [search, setSearch] = useState("");
  const token = localStorage.getItem("token");
  useEffect(() => {
    setLoading(true);
    api.get("/students/deleted/fees-status")
      .then((r) => { setRows(r.data.students || []); setMode(r.data.mode || "left_page"); })
      .catch(() => {}).finally(() => setLoading(false));
  }, []);
  const q = search.toLowerCase();
  const filtered = rows.filter((s) => !q || [s.name, s.admission_number].some((v) => (v || "").toLowerCase().includes(q)));
  const t = filtered.reduce((a, s) => ({ total: a.total + (s.total || 0), paid: a.paid + (s.paid || 0), due: a.due + (s.due || 0) }), { total: 0, paid: 0, due: 0 });
  const slipUrl = (id, dl) => `${API}/students/${id}/fee-slip-pdf?token=${token}${dl ? "&download=1" : ""}`;
  return (
    <div className="space-y-4" data-testid="fees-status-tab">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input className="pl-9 h-9" placeholder="Search name, adm no…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="fees-status-search" />
        </div>
        <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-slate-100 text-slate-600" data-testid="fees-mode-badge">
          Counting: {mode === "current_session" ? "Current Session (live)" : "Left Students Page (at removal)"}
        </span>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-xl border border-slate-200 bg-white p-3"><div className="text-xs text-slate-500">Total Fees</div><div className="text-lg font-extrabold tabular text-slate-800" data-testid="fs-total">{fmt(t.total)}</div></div>
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3"><div className="text-xs text-emerald-700">Paid Fees</div><div className="text-lg font-extrabold tabular text-emerald-700" data-testid="fs-paid">{fmt(t.paid)}</div></div>
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-3"><div className="text-xs text-rose-700">Due Fees</div><div className="text-lg font-extrabold tabular text-rose-700" data-testid="fs-due">{fmt(t.due)}</div></div>
      </div>
      <div className="bg-white rounded-xl border border-slate-200 overflow-auto max-h-[calc(100vh-380px)]">
        {loading ? <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-indigo-500" /></div> :
          filtered.length === 0 ? <div className="py-16 text-center text-slate-400">No left students.</div> : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider sticky top-0 z-10">
              <tr>
                <th className="px-3 py-2.5 text-left">Student</th>
                <th className="px-3 py-2.5 text-left">Class</th>
                <th className="px-3 py-2.5 text-right">Total Fees</th>
                <th className="px-3 py-2.5 text-right">Paid Fees</th>
                <th className="px-3 py-2.5 text-right">Due Fees</th>
                <th className="px-3 py-2.5 text-center">Fee Slip</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((s) => (
                <tr key={s.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`fees-row-${s.admission_number}`}>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-full bg-slate-100 text-slate-500 overflow-hidden flex items-center justify-center text-xs font-semibold border border-slate-200 shrink-0">
                        {s.photo ? <img src={s.photo} alt={s.name} className="w-full h-full object-cover" /> : initials(s.name)}
                      </div>
                      <div><div className="font-medium text-slate-800">{s.name}</div><div className="text-xs text-slate-400">{s.admission_number}</div></div>
                    </div>
                  </td>
                  <td className="px-3 py-2.5 text-slate-600">{`${s.class_name} ${s.section}`.trim() || "—"}</td>
                  <td className="px-3 py-2.5 text-right tabular font-medium">{fmt(s.total)}</td>
                  <td className="px-3 py-2.5 text-right tabular text-emerald-600">{fmt(s.paid)}</td>
                  <td className={`px-3 py-2.5 text-right tabular font-semibold ${s.due > 0 ? "text-rose-600" : "text-emerald-600"}`}>{fmt(s.due)}</td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center justify-center gap-1.5">
                      <Button asChild size="sm" variant="outline" className="gap-1.5 h-8" data-testid={`slip-view-${s.admission_number}`}>
                        <a href={slipUrl(s.id, false)} target="_blank" rel="noreferrer"><Eye size={14} />View</a>
                      </Button>
                      <Button asChild size="sm" variant="outline" className="gap-1.5 h-8" data-testid={`slip-download-${s.admission_number}`}>
                        <a href={slipUrl(s.id, true)}><Download size={14} />Download</a>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function LeftSettingsTab({ settings, onSaved }) {
  const [form, setForm] = useState(settings || {});
  const [saving, setSaving] = useState(false);
  useEffect(() => { setForm(settings || {}); }, [settings]);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const save = async () => {
    setSaving(true);
    try { await api.put("/settings", form); toast.success("Left-student settings saved"); onSaved && onSaved(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <div className="max-w-2xl space-y-5" data-testid="left-settings-tab">
      <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-5" data-testid="left-settings-card">
        <div>
          <Label className="text-sm font-semibold text-slate-700">Fee Slip starts</Label>
          <p className="text-xs text-slate-400 mb-1.5">Prefix shown before the admission number on each fee slip (e.g. FS → FS1024).</p>
          <Input value={form.left_fees_slip_prefix ?? "FS"} onChange={(e) => set("left_fees_slip_prefix", e.target.value)} className="h-9 max-w-xs" data-testid="set-slip-prefix" />
        </div>
        <div>
          <Label className="text-sm font-semibold text-slate-700">Fee Slip contents</Label>
          <p className="text-xs text-slate-400 mb-1.5">Choose what each printed fee slip includes.</p>
          <Select value={form.left_fees_slip_summary || "breakdown"} onValueChange={(v) => set("left_fees_slip_summary", v)}>
            <SelectTrigger className="h-9 max-w-xs" data-testid="set-slip-summary"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="summary">Summary only (Total / Paid / Due)</SelectItem>
              <SelectItem value="breakdown">Summary + fee-head breakdown</SelectItem>
              <SelectItem value="history">Summary + breakdown + payment history</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-sm font-semibold text-slate-700">Show / Count Left Students' Fees</Label>
          <p className="text-xs text-slate-400 mb-2">Decide where left students' fees are counted.</p>
          <RadioGroup value={form.left_fees_count || "left_page"} onValueChange={(v) => set("left_fees_count", v)} className="space-y-2" data-testid="set-fees-count">
            <label htmlFor="lfc-current" className="flex items-start gap-3 rounded-lg border border-slate-200 p-3 cursor-pointer hover:border-indigo-300 transition-colors">
              <RadioGroupItem value="current_session" id="lfc-current" className="mt-0.5" data-testid="fees-count-current" />
              <span><span className="text-sm font-medium text-slate-800 block">In Current Session</span><span className="text-xs text-slate-400">Left students' fees are counted along with regular students in the current session.</span></span>
            </label>
            <label htmlFor="lfc-left" className="flex items-start gap-3 rounded-lg border border-slate-200 p-3 cursor-pointer hover:border-indigo-300 transition-colors">
              <RadioGroupItem value="left_page" id="lfc-left" className="mt-0.5" data-testid="fees-count-left" />
              <span><span className="text-sm font-medium text-slate-800 block">In Only Left Student Section</span><span className="text-xs text-slate-400">Left students' dues are shown and counted only in the Left Students section, separate from regular students.</span></span>
            </label>
          </RadioGroup>
        </div>
        <div className="flex items-center justify-between border-t border-slate-100 pt-4">
          <div className="pr-4">
            <Label className="text-sm font-semibold text-slate-700">Allow editing Due amount (admin only)</Label>
            <p className="text-xs text-slate-400">Let admins edit the "Due at Removal" directly in the Left Students table.</p>
          </div>
          <Switch checked={form.left_due_editable !== false} onCheckedChange={(v) => set("left_due_editable", v)} data-testid="set-due-editable" />
        </div>
      </div>
      <Button onClick={save} disabled={saving} className="gap-1.5" data-testid="save-left-settings">
        {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}Save Settings
      </Button>
    </div>
  );
}

